import { forwardRef, useEffect, useImperativeHandle } from 'react';
import BaseForm from 'components/atoms/base-form';
import BaseFormItem from 'components/atoms/base-form/item';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import BaseGrid from 'components/atoms/base-grid';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';
import BaseDatePicker from 'components/atoms/base-datepicker';
import BaseCheckbox from 'components/atoms/base-checkbox';
import { DateFormatter } from 'infrastructure/functions';

import { validationSchema } from './validation-schema';
import { cohorts } from '../cohorts';

import type { IAdherenceSettingCohortsInactiveDays } from 'infrastructure/interfaces';

export interface IAdherenceSettingsCohortsInactiveDaysRef {
  submit: () => Promise<void>;
  reset: () => void;
}

interface IProps {
  data?: IAdherenceSettingCohortsInactiveDays;
  loading?: boolean;
  onSave?: (val: IAdherenceSettingCohortsInactiveDays) => void;
}

const AdherenceSettingsCohortsInactiveDaysForm = forwardRef<
  IAdherenceSettingsCohortsInactiveDaysRef,
  IProps
>((props, ref) => {
  const { data, loading = false, onSave } = props;

  const { t } = useTranslation();

  const cohortsArr = Object.keys(cohorts).map((el) => +el);

  const formik = useFormik<IAdherenceSettingCohortsInactiveDays>({
    initialValues: {
      guid: '',
      from: '',
      to: '',
      inactiveAdherenceCohorts: [],
    } as IAdherenceSettingCohortsInactiveDays,
    onSubmit: async (values) => {
      onSave?.(values);
    },
    validateOnChange: false,
    validationSchema: validationSchema(),
  });

  const { submitForm, resetForm, values } = formik;

  const getCohortChecked = (num: number) =>
    values.inactiveAdherenceCohorts.includes(num);

  const checkCohort = (num: number, checked: boolean) => {
    let inactiveAdherenceCohorts = [...values.inactiveAdherenceCohorts];
    if (!checked) {
      inactiveAdherenceCohorts = inactiveAdherenceCohorts.filter(
        (el) => el !== num,
      );
    } else {
      inactiveAdherenceCohorts.push(num);
    }

    formik.setFieldValue('inactiveAdherenceCohorts', inactiveAdherenceCohorts);
  };

  const selectAll = (checked: boolean) => {
    formik.setFieldValue('inactiveAdherenceCohorts', checked ? cohortsArr : []);
  };

  const changeDate = (
    date: Date | null | undefined,
    direction: 'from' | 'to',
  ) => {
    formik.setFieldValue(direction, date ? DateFormatter({ date }) : '');
  };

  useImperativeHandle(ref, () => ({
    submit: submitForm,
    reset: resetForm,
  }));

  useEffect(() => {
    if (data) formik.resetForm({ values: data });
  }, [data]);

  return (
    <BaseSpinWrapper spinning={loading}>
      <BaseForm
        loading={loading}
        formik={formik}
        plaintext={false}
        readonly={false}
        withControls
      >
        <BaseGrid columns={2}>
          <BaseFormItem name="from" label={t('labels.from')}>
            <BaseDatePicker
              value={values.from ? new Date(values.from) : undefined}
              maxDate={new Date(values.to)}
              onChange={(val) => changeDate(val, 'from')}
            />
          </BaseFormItem>
          <BaseFormItem name="to" label={t('labels.to')}>
            <BaseDatePicker
              value={values.to ? new Date(values.to) : undefined}
              minDate={new Date(values.from)}
              onChange={(val) => changeDate(val, 'to')}
            />
          </BaseFormItem>
        </BaseGrid>

        <BaseFormItem
          name="inactiveAdherenceCohorts"
          label={t('labels.inactiveCohorts')}
        >
          <BaseGrid columns={2}>
            {cohortsArr.map((el) => (
              <BaseCheckbox
                value={getCohortChecked(el)}
                key={el}
                placeholder={`${el} - ${cohorts[el]}`}
                onChange={(val) => checkCohort(el, val)}
              />
            ))}
            <BaseCheckbox
              value={
                values.inactiveAdherenceCohorts.length === cohortsArr.length
              }
              placeholder={t('labels.selectAll')}
              onChange={(val) => selectAll(val)}
            />
          </BaseGrid>
        </BaseFormItem>
      </BaseForm>
    </BaseSpinWrapper>
  );
});

AdherenceSettingsCohortsInactiveDaysForm.displayName =
  'AdherenceSettingsCohortsInactiveDays';

export default AdherenceSettingsCohortsInactiveDaysForm;
